import { cva } from 'class-variance-authority'
import { Label } from '~/components/UI/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/UI/shadcn/select'
import { cn } from '~/lib/utils'

import { useQuery } from '@tanstack/react-query'
import { getFilters } from '~/components/Content/Filters/data'
import { parseAsString, useQueryState } from 'nuqs'
import { useEffect } from 'react'

type Dropdown = {
  data: {
    // id: string
    label?: string
    required: boolean
    note?: string
  }
  prefix?: string
  items?: {
    label?: string
    id?: string
  }
  options?: {
    label?: string
    id: string
  }[]
  style?: 'Default' | 'Outline' | 'Minimal' | null | undefined
  disabled?: boolean | false
  queryKey?: string
  defaultValue?: string
}

const dropdownStyle = cva('bg-white border-2', {
  variants: {
    style: {
      Default: 'border-transparent disabled:bg-grey-m disabled:text-blue-d-80',
      Outline: 'border-blue-d-40 disabled:bg-grey-m disabled:text-blue-d-80',
      Minimal:
        'border-b-2 border-blue-d-40 border-x-0 border-t-0 disabled:bg-white disabled:placeholder:text-grey-m disabled:text-grey-m disabled:border-grey-m'
    }
  },
  defaultVariants: {
    style: 'Default'
  }
})

const Dropdown = ({
  data,
  items,
  prefix,
  options,
  style,
  disabled,
  queryKey,
  defaultValue
}: Dropdown) => {
  const { id } = items || {}
  const [query, setQuery] = useQueryState<string>(
    queryKey || 'filters',
    parseAsString
  )
  const normalisedQuery = query || ''

  const { data: filterData } = useQuery({
    queryKey: [id],
    queryFn: () => getFilters(id || ''),
    enabled: !!id // Only fetch if id exists
  })

  useEffect(() => {
    if (defaultValue) setQuery(defaultValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // if (isLoading) {
  //   return <div>Loading...</div>
  // }

  return (
    <div className='gap-xs flex flex-col'>
      {data?.label && (
        <Label
          // htmlFor={data.id}
          className='flex justify-between'
        >
          {data.label}{' '}
          {data?.required && <span className='text-blue-m'>*</span>}
        </Label>
      )}
      <Select
        value={normalisedQuery}
        onValueChange={value => {
          setQuery(value)
        }}
      >
        <SelectTrigger
          aria-label='Select an option'
          className={cn(
            dropdownStyle({ style }),
            'gap-xs p-xs lg:p-xs-plus hover:border-blue-d-80 h-auto text-left transition-colors disabled:opacity-100'
          )}
          disabled={disabled}
        >
          <SelectValue placeholder='Select an option' />
        </SelectTrigger>
        <SelectContent className='-mt-3xs max-h-[300px] w-[var(--radix-select-trigger-width)] overflow-y-auto'>
          {filterData &&
            'items' in filterData &&
            Array.isArray(filterData.items) &&
            filterData.items.map(item => (
              <SelectItem
                key={item.id}
                value={item.id}
                className='px-xs lg:px-xs-plus py-xs text-blue-d cursor-pointer hover:underline'
              >
                {`${(prefix ? prefix + ' ' : '') + item.label}`}
              </SelectItem>
            ))}
          {options &&
            options.map(item => (
              <SelectItem
                key={item.id}
                value={item.id}
                defaultValue={defaultValue || ''}
                className='px-xs lg:px-xs-plus py-xs text-blue-d cursor-pointer hover:underline'
              >
                {item.label}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      {data?.note && <p className='text-xs'>{data?.note}</p>}
    </div>
  )
}

export default Dropdown
